'use client';

import { useEffect, useRef, useState, useMemo } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { Card } from '@/components/ui/card';
import {
    Stethoscope,
    Microscope,
    Scissors,
    Droplets,
    Wrench,
    CheckCircle,
    Clock,
    Activity
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function TreatmentStepsSection() {
    const { t, isLoading } = useTranslation('root-canal');
    const { isRTL } = useRTL();
    const [isMounted, setIsMounted] = useState(false);
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const timelineRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (!sectionRef.current) return;

        const ctx = gsap.context(() => {
            // Title animation
            if (titleRef.current) {
                gsap.fromTo(titleRef.current,
                    { opacity: 0, y: 50 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: titleRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Timeline steps animation with individual triggers
            if (timelineRef.current?.children) {
                const steps = Array.from(timelineRef.current.children);
                steps.forEach((step, index) => {
                    gsap.fromTo(step,
                        {
                            opacity: 0,
                            x: index % 2 === 0 ? -120 : 120,
                            scale: 0.8,
                            y: 30
                        },
                        {
                            opacity: 1,
                            x: 0,
                            scale: 1,
                            y: 0,
                            duration: 0.8,
                            ease: "power3.out",
                            scrollTrigger: {
                                trigger: step,
                                start: "top 85%",
                                end: "bottom 15%",
                                toggleActions: "play none none reverse"
                            }
                        }
                    );
                });
            }

            // Continuous pulsing animations for icons
            const pulseIcons = sectionRef.current?.querySelectorAll(".pulse-icon");
            if (pulseIcons && pulseIcons.length > 0) {
                gsap.to(pulseIcons, {
                    scale: 1.1,
                    duration: 1.5,
                    repeat: -1,
                    yoyo: true,
                    ease: "power2.inOut"
                });
            }

            // Continuous rotation for bacteria icons
            const rotateIcons = sectionRef.current?.querySelectorAll(".rotate-icon");
            if (rotateIcons && rotateIcons.length > 0) {
                gsap.to(rotateIcons, {
                    rotation: 360,
                    duration: 3,
                    repeat: -1,
                    ease: "none"
                });
            }

        }, sectionRef);

        return () => {
            ctx.revert();
        };
    }, []);

    const steps = useMemo(() => [
        {
            step: 1,
            title: t('treatmentSteps.steps.examination.title'),
            description: t('treatmentSteps.steps.examination.description'),
            duration: t('treatmentSteps.steps.examination.duration'),
            icon: <Stethoscope className="w-8 h-8" />,
            animation: "pulse-icon",
            illustration: "🔍", // Magnifying glass for examination
            color: "from-blue-500 to-blue-600"
        },
        {
            step: 2,
            title: t('treatmentSteps.steps.anesthesia.title'),
            description: t('treatmentSteps.steps.anesthesia.description'),
            duration: t('treatmentSteps.steps.anesthesia.duration'),
            icon: <Droplets className="w-8 h-8" />,
            animation: "pulse-icon",
            illustration: "💉", // Syringe for anesthesia
            color: "from-green-500 to-green-600"
        },
        {
            step: 3,
            title: t('treatmentSteps.steps.removal.title'),
            description: t('treatmentSteps.steps.removal.description'),
            duration: t('treatmentSteps.steps.removal.duration'),
            icon: <Scissors className="w-8 h-8" />,
            animation: "rotate-icon",
            illustration: "🦠", // Bacteria being removed
            color: "from-red-500 to-red-600"
        },
        {
            step: 4,
            title: t('treatmentSteps.steps.cleaning.title'),
            description: t('treatmentSteps.steps.cleaning.description'),
            duration: t('treatmentSteps.steps.cleaning.duration'),
            icon: <Microscope className="w-8 h-8" />,
            animation: "pulse-icon",
            illustration: "🧼", // Cleaning symbol
            color: "from-purple-500 to-purple-600"
        },
        {
            step: 5,
            title: t('treatmentSteps.steps.filling.title'),
            description: t('treatmentSteps.steps.filling.description'),
            duration: t('treatmentSteps.steps.filling.duration'),
            icon: <Wrench className="w-8 h-8" />,
            animation: "",
            illustration: "🔧", // Tool for filling
            color: "from-orange-500 to-orange-600"
        },
        {
            step: 6,
            title: t('treatmentSteps.steps.crown.title'),
            description: t('treatmentSteps.steps.crown.description'),
            duration: t('treatmentSteps.steps.crown.duration'),
            icon: <CheckCircle className="w-8 h-8" />,
            animation: "pulse-icon",
            illustration: "👑", // Crown symbol
            color: "from-yellow-500 to-yellow-600"
        }
    ], [t]);

    return (
        <section id="treatment-steps" ref={sectionRef} className="py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-800 dark:to-slate-900">
            <div className="container">
                <div ref={titleRef} className="text-center mb-16">
                    {!isMounted || isLoading ? (
                        <div className="animate-pulse">
                            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mb-4 max-w-2xl mx-auto"></div>
                            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-8 max-w-3xl mx-auto"></div>
                        </div>
                    ) : (
                        <>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif mb-6">
                                <span className="text-gold">{t('treatmentSteps.title.part1')}</span> {t('treatmentSteps.title.part2')}
                            </h2>
                            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
                                {t('treatmentSteps.subtitle')}
                            </p>
                        </>
                    )}
                </div>

                <div ref={timelineRef} className="relative max-w-5xl mx-auto">
                    {!isMounted || isLoading ? (
                        <div className="animate-pulse space-y-8">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="flex items-center mb-12">
                                    <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full mr-6"></div>
                                    <div className="flex-1">
                                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <>
                            {/* Central timeline line */}
                            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gold rounded-full hidden md:block"></div>

                            {steps.map((step, index) => (
                        <div
                            key={step.step}
                            className={`relative flex items-center mb-12 ${
                                isRTL
                                    ? (index % 2 === 0 ? 'md:flex-row-reverse' : 'md:flex-row')
                                    : (index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse')
                                }`}
                        >
                            {/* Timeline dot */}
                            <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-gold rounded-full border-4 border-white dark:border-slate-900 z-10 hidden md:block shadow-lg">
                                <div className="absolute inset-0 rounded-full bg-gold animate-ping opacity-20"></div>
                            </div>

                            {/* Step card */}
                            <Card className={`group relative w-full md:w-[calc(50%-3rem)] p-8 bg-white dark:bg-slate-800 border-2 border-transparent hover:border-gold transition-all duration-300 hover:shadow-xl hover:shadow-gold/20 ${
                                isRTL 
                                    ? (index % 2 === 0 ? 'md:ml-12' : 'md:mr-12')
                                    : (index % 2 === 0 ? 'md:mr-12' : 'md:ml-12')
                                }`}>
                                {/* Background gradient */}
                                <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-lg`}></div>

                                <div className="relative z-10">
                                    {/* Step header */}
                                    <div className={`flex items-center justify-between mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <div className={`flex items-center gap-4`}>
                                            <div className={`w-16 h-16 bg-gradient-to-br ${step.color} rounded-xl flex items-center justify-center text-white shadow-lg`}>
                                                <div className={step.animation}>
                                                    {step.icon}
                                                </div>
                                            </div>
                                            <div className="text-4xl">{step.illustration}</div>
                                        </div>

                                        <div className={isRTL ? 'text-right' : 'text-right'}>
                                            <div className="text-3xl font-bold text-gold mb-1">
                                                {isRTL ? step.step.toLocaleString('ar-EG') : step.step}
                                            </div>
                                            <div className={`flex items-center gap-1 text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <span>{step.duration}</span>
                                                <Clock className="w-4 h-4" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Step content */}
                                    <div>
                                        <h3 className="text-xl font-bold text-navy dark:text-white mb-3 group-hover:text-gold transition-colors duration-300">
                                            {step.title}
                                        </h3>
                                        <p className={`text-slate-600 dark:text-slate-300 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                                            {step.description}
                                        </p>
                                    </div>

                                    {/* Progress indicator */}
                                    <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-600">
                                        <div className="flex justify-between items-center text-sm text-slate-600 dark:text-slate-300">
                                            <span>{t('treatmentSteps.progressText.step')} {isRTL ? step.step.toLocaleString('ar-EG') : step.step} {t('treatmentSteps.progressText.of')} {isRTL ? steps.length.toLocaleString('ar-EG') : steps.length}</span>
                                            <div className={`flex gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                {Array.from({ length: steps.length }).map((_, i) => (
                                                    <div
                                                        key={i}
                                                        className={`w-2 h-2 rounded-full ${i < step.step ? 'bg-gold' : 'bg-slate-200 dark:bg-slate-600'
                                                            }`}
                                                    ></div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </div>
                            ))}
                        </>
                    )}
                </div>

                {/* Summary section */}
                {!isMounted || isLoading ? (
                    <div className="mt-16 text-center animate-pulse">
                        <div className="max-w-4xl mx-auto p-8 bg-gray-200 dark:bg-gray-700 rounded-xl">
                            <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded mb-4 max-w-md mx-auto"></div>
                            <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded max-w-2xl mx-auto"></div>
                        </div>
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        viewport={{ once: true }}
                        className="mt-16 text-center"
                    >
                        <Card className="max-w-4xl mx-auto bg-gold/10 border-gold/30">
                            <div className="p-8">
                                <div className={`flex items-center justify-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <Activity className="w-8 h-8 text-gold" />
                                    <h3 className="text-2xl font-bold text-navy dark:text-white">
                                        {t('treatmentSteps.summary.title')}
                                    </h3>
                                </div>
                                <p className={`text-slate-600 dark:text-slate-300 leading-relaxed text-lg ${isRTL ? 'text-right' : 'text-center'}`}>
                                    {t('treatmentSteps.summary.description')}
                                </p>
                            </div>
                        </Card>
                    </motion.div>
                )}
            </div>
        </section>
    );
} 